// ---------------------------------
// client/src/utils/jobProgress.js
// Helpers for deriving job progress state and summaries
// ---------------------------------

const STATUS = {
    NOT_STARTED: 'not_started',
    IN_PROGRESS: 'in_progress',
    COMPLETED: 'completed',
    ARCHIVED: 'archived',
};

function normaliseFormStatus(form) {
    const status = (form?.status || '').toLowerCase();
    if (status === 'submitted') return 'completed';
    if (status === 'draft') {
        const updatedAt = form?.updatedAt ? new Date(form.updatedAt).getTime() : 0;
        const createdAt = form?.createdAt ? new Date(form.createdAt).getTime() : 0;
        if (updatedAt && createdAt && updatedAt > createdAt) {
            return 'in_progress';
        }
        return 'pending';
    }
    return 'pending';
}

export function computeJobProgress(job) {
    const forms = Array.isArray(job?.pdfForms) ? job.pdfForms : [];
    const counts = {
        total: forms.length,
        completed: 0,
        inProgress: 0,
        pending: 0,
    };
    let latestActivity = job?.updatedAt ? new Date(job.updatedAt).getTime() : 0;
    const jobTypeIdSet = new Set();
    const jobTypeNameSet = new Set();

    const primaryJobTypeId = job?.jobType?.id ?? job?.jobTypeId ?? null;
    if (primaryJobTypeId) {
        jobTypeIdSet.add(String(primaryJobTypeId));
    }
    const primaryJobTypeName =
        typeof job?.jobType?.name === 'string' && job.jobType.name.trim() ? job.jobType.name.trim() : '';
    if (primaryJobTypeName) {
        jobTypeNameSet.add(primaryJobTypeName);
    }

    const jobTypeIds = Array.isArray(job?.jobTypeIds) ? job.jobTypeIds : [];
    jobTypeIds.forEach((id) => {
        if (id) {
            jobTypeIdSet.add(String(id));
        }
    });

    (job?.jobTypes || []).forEach((jobType) => {
        if (jobType?.id) {
            jobTypeIdSet.add(String(jobType.id));
        }
        if (typeof jobType?.name === 'string' && jobType.name.trim()) {
            jobTypeNameSet.add(jobType.name.trim());
        }
    });

    forms.forEach((form) => {
        const status = normaliseFormStatus(form);
        if (status === 'completed') {
            counts.completed += 1;
        } else if (status === 'in_progress') {
            counts.inProgress += 1;
        } else {
            counts.pending += 1;
        }
        if (form?.updatedAt) {
            const timestamp = new Date(form.updatedAt).getTime();
            if (!Number.isNaN(timestamp) && timestamp > latestActivity) {
                latestActivity = timestamp;
            }
        }
    });

    const jobStatus = (job?.status || '').toLowerCase();

    let derivedStatus = STATUS.NOT_STARTED;
    if (jobStatus === 'archived') {
        derivedStatus = STATUS.ARCHIVED;
    } else if (jobStatus === 'completed') {
        derivedStatus = STATUS.COMPLETED;
    } else if (counts.total === 0) {
        derivedStatus = STATUS.NOT_STARTED;
    } else if (counts.inProgress > 0 || counts.completed > 0) {
        derivedStatus = STATUS.IN_PROGRESS;
    } else {
        derivedStatus = STATUS.NOT_STARTED;
    }

    const progressPercent = counts.total === 0 ? 0 : Math.round((counts.completed / counts.total) * 100);
    const jobTypeIdsOut = Array.from(jobTypeIdSet);
    const jobTypeNames = Array.from(jobTypeNameSet);
    const jobTypeName = jobTypeNames.join(', ');

    return {
        jobId: job?.id,
        jobNumber: job?.jobNumber || '',
        jobTypeName,
        jobTypeNames,
        jobTypeIds: jobTypeIdsOut,
        status: derivedStatus,
        counts,
        lastActivity: latestActivity,
        progressPercent,
        raw: job,
    };
}

export function summariseJobs(jobs) {
    const initial = {
        totalJobs: 0,
        notStarted: 0,
        inProgress: 0,
        completed: 0,
        totalForms: 0,
        completedForms: 0,
    };

    return (Array.isArray(jobs) ? jobs : []).reduce((acc, job) => {
        const progress = computeJobProgress(job);
        if (progress.status === STATUS.ARCHIVED) {
            return acc;
        }
        acc.totalJobs += 1;
        acc.totalForms += progress.counts.total;
        acc.completedForms += progress.counts.completed;
        if (progress.status === STATUS.COMPLETED) {
            acc.completed += 1;
        } else if (progress.status === STATUS.IN_PROGRESS) {
            acc.inProgress += 1;
        } else {
            acc.notStarted += 1;
        }
        return acc;
    }, initial);
}

export function groupJobsByStatus(jobs) {
    const grouped = {
        [STATUS.IN_PROGRESS]: [],
        [STATUS.NOT_STARTED]: [],
        [STATUS.COMPLETED]: [],
        [STATUS.ARCHIVED]: [],
    };
    (Array.isArray(jobs) ? jobs : []).forEach((job) => {
        const progress = computeJobProgress(job);
        grouped[progress.status].push(progress);
    });

    Object.keys(grouped).forEach((key) => {
        grouped[key].sort((a, b) => b.lastActivity - a.lastActivity);
    });

    return grouped;
}

export const JOB_STATUS_LABELS = {
    [STATUS.NOT_STARTED]: 'טרם התחיל',
    [STATUS.IN_PROGRESS]: 'בתהליך',
    [STATUS.COMPLETED]: 'הושלם',
    [STATUS.ARCHIVED]: 'בארכיון',
};

export const JOB_STATUS_ORDER = [STATUS.IN_PROGRESS, STATUS.NOT_STARTED, STATUS.COMPLETED];

export const ACTIVE_JOB_STATUS_ORDER = [...JOB_STATUS_ORDER];

export const JOB_STATUS_ARCHIVED = STATUS.ARCHIVED;
